import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../../services/api';
import { Button } from '../../../components/ui';
import { CheckIcon, PencilIcon, SpinnerIcon, TrashIcon, XIcon } from '../../../components/ui/Icons';
import ConfirmationDialog from '../../../components/ui/ConfirmationDialog';
import { useAuth } from '../../../contexts/AuthContext';
import { useNotification } from '../../../contexts/NotificationContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { formatDateTime } from '../../../config/appConfig';
import { cn } from '../../../lib/utils';
import { Z_INDEX } from '../../../lib/zLayers';
import type { EmailComment, EmailMentionableUsersResponse } from '../useData';
import { emailPartitionAtSearch } from '../utils/emailPartitionAtQuery';
import MentionTextarea from './MentionTextarea';
import CollaborationNoteBody from './CollaborationNoteBody';
import CollaborationGuideInfo from './CollaborationGuideInfo';

interface EmailCommentsPanelProps {
  emailId: number;
  partitionAt?: string;
  onCommentAdded?: () => void;
  /** Drawer: fill height with scrollable list; inline: bordered block under header */
  layout?: 'inline' | 'drawer';
}

function canAuthorModifyComment(
  comment: EmailComment,
  comments: EmailComment[],
  currentUserId: number | undefined,
): boolean {
  if (currentUserId == null || comment.userId !== currentUserId) return false;
  const last = comments[comments.length - 1];
  return last?.id === comment.id;
}

const EmailCommentsPanel: React.FC<EmailCommentsPanelProps> = ({
  emailId,
  partitionAt,
  onCommentAdded,
  layout = 'inline',
}) => {
  const { user } = useAuth();
  const { colors } = useTheme();
  const { t } = useTranslation('emails');
  const { showError } = useNotification();
  const [comments, setComments] = useState<EmailComment[]>([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<EmailComment | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [mentionData, setMentionData] = useState<EmailMentionableUsersResponse | null>(null);
  const partitionQuery = emailPartitionAtSearch(partitionAt);

  const mentionTextareaClass = cn(
    'w-full px-3 py-2 text-sm border rounded-lg min-h-[72px] placeholder:opacity-70 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500',
    colors.surface,
    colors.border,
    colors.text,
  );

  useEffect(() => {
    let cancelled = false;
    api
      .get<EmailMentionableUsersResponse>(`/emails/${emailId}/mentionable-users${partitionQuery}`)
      .then((res) => {
        if (!cancelled) setMentionData(res.data);
      })
      .catch(() => {
        if (!cancelled) setMentionData({ users: [], assignee: null });
      });
    return () => {
      cancelled = true;
    };
  }, [emailId, partitionQuery]);

  const loadComments = async () => {
    setLoading(true);
    try {
      const { data } = await api.get<EmailComment[]>(`/emails/${emailId}/comments${partitionQuery}`);
      setComments(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load comments', err);
      setComments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadComments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailId, partitionAt]);

  const handleSubmit = async () => {
    const body = draft.trim();
    if (!body) return;
    setSaving(true);
    try {
      const { data } = await api.post<EmailComment>(`/emails/${emailId}/comments${partitionQuery}`, { body });
      setComments((prev) => [...prev, data]);
      setDraft('');
      onCommentAdded?.();
    } catch (err) {
      console.error('Failed to post comment', err);
      showError(t('commentsPostFailed'));
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (comment: EmailComment) => {
    setEditingCommentId(comment.id);
    setEditDraft(comment.body);
  };

  const cancelEdit = () => {
    setEditingCommentId(null);
    setEditDraft('');
  };

  const handleSaveEdit = async () => {
    if (editingCommentId == null) return;
    const body = editDraft.trim();
    if (!body) return;
    setSavingEdit(true);
    try {
      const { data } = await api.patch<EmailComment>(
        `/emails/${emailId}/comments/${editingCommentId}${partitionQuery}`,
        { body },
      );
      setComments((prev) => prev.map((c) => (c.id === data.id ? data : c)));
      cancelEdit();
      onCommentAdded?.();
    } catch (err) {
      console.error('Failed to update comment', err);
      showError(t('commentsEditFailed'));
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/emails/${emailId}/comments/${deleteTarget.id}${partitionQuery}`);
      setComments((prev) => prev.filter((c) => c.id !== deleteTarget.id));
      if (editingCommentId === deleteTarget.id) cancelEdit();
      setDeleteTarget(null);
      onCommentAdded?.();
    } catch (err) {
      console.error('Failed to delete comment', err);
      showError(t('commentsDeleteFailed'));
    } finally {
      setDeleting(false);
    }
  };

  const isDrawer = layout === 'drawer';
  const mentionMenuZIndex = isDrawer ? Z_INDEX.modal + 1 : Z_INDEX.portaledDropdown;
  const internalHint = useMemo(() => t('commentsInternalHint'), [t]);

  return (
    <div
      className={cn(
        isDrawer
          ? 'flex min-h-0 flex-1 flex-col px-4 py-3'
          : cn(
              'border-x border-b px-4 sm:px-6 py-3 rounded-b-lg',
              'bg-slate-50/90 dark:bg-slate-900/30 border-slate-200/70 dark:border-slate-700/50',
            ),
      )}
    >
      {!isDrawer && (
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="text-sm font-medium text-slate-800 dark:text-slate-200">{t('comments')}</span>
            <CollaborationGuideInfo />
          </div>
          <span className={cn('text-xs text-right', colors.textMuted)}>{t('commentsMentionHint')}</span>
        </div>
      )}
      {isDrawer && (
        <p className={cn('text-xs mb-2', colors.textMuted)}>{internalHint}</p>
      )}

      <div className={cn(isDrawer && 'min-h-0 flex-1 overflow-y-auto')}>
        {loading ? (
          <p className={cn('text-sm', colors.textMuted)}>{t('commentsLoading')}</p>
        ) : comments.length === 0 ? (
          <p className={cn('text-sm mb-3', colors.textMuted)}>{t('commentsEmpty')}</p>
        ) : (
          <ul className={cn('space-y-3 mb-3', isDrawer ? 'pb-2' : 'max-h-64 overflow-y-auto')}>
            {comments.map((c) => {
              const canModify = canAuthorModifyComment(c, comments, user?.id);
              const isEditing = editingCommentId === c.id;

              return (
                <li key={c.id} className={cn('rounded-lg border px-3 py-2', colors.surface, colors.border)}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <span className="text-sm font-medium">
                        {c.user.name}
                        {c.userId === user?.id ? ` ${t('commentsYou')}` : ''}
                      </span>
                      <div className={cn('text-xs mt-0.5', colors.textMuted)}>
                        {formatDateTime(c.createdAt)}
                        {c.editedAt ? ` · ${t('commentsEdited')}` : ''}
                      </div>
                    </div>
                    {canModify && !isEditing && (
                      <div className="flex items-center gap-0.5 flex-shrink-0">
                        {isDrawer ? (
                          <>
                            <button
                              type="button"
                              onClick={() => startEdit(c)}
                              title={t('commentsEdit')}
                              aria-label={t('commentsEdit')}
                              className={cn('p-1.5 rounded', colors.textMuted, colors.interactiveHover)}
                            >
                              <PencilIcon className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setDeleteTarget(c)}
                              title={t('commentsDelete')}
                              aria-label={t('commentsDelete')}
                              className="p-1.5 rounded text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              type="button"
                              onClick={() => startEdit(c)}
                              className={cn('text-xs px-2 py-1 rounded', colors.textMuted, colors.interactiveHover)}
                            >
                              {t('commentsEdit')}
                            </button>
                            <button
                              type="button"
                              onClick={() => setDeleteTarget(c)}
                              className="text-xs px-2 py-1 rounded text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                            >
                              {t('commentsDelete')}
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  {isEditing ? (
                    <div className="mt-2">
                      <MentionTextarea
                        emailId={emailId}
                        partitionAt={partitionAt}
                        mentionData={mentionData}
                        value={editDraft}
                        onChange={setEditDraft}
                        className={mentionTextareaClass}
                        rows={3}
                        disabled={savingEdit}
                        menuZIndex={mentionMenuZIndex}
                      />
                      <div className="flex items-center gap-1 mt-2">
                        {isDrawer ? (
                          <>
                            <button
                              type="button"
                              disabled={savingEdit || !editDraft.trim()}
                              onClick={() => void handleSaveEdit()}
                              title={savingEdit ? t('commentsSaving') : t('commentsSave')}
                              aria-label={savingEdit ? t('commentsSaving') : t('commentsSave')}
                              className={cn(
                                'p-1.5 rounded text-indigo-600 hover:bg-indigo-50 dark:text-indigo-400 dark:hover:bg-indigo-950/30',
                                'disabled:opacity-50 disabled:cursor-not-allowed',
                              )}
                            >
                              {savingEdit ? (
                                <SpinnerIcon className="w-4 h-4" />
                              ) : (
                                <CheckIcon className="w-4 h-4" />
                              )}
                            </button>
                            <button
                              type="button"
                              disabled={savingEdit}
                              onClick={cancelEdit}
                              title={t('commentsCancel')}
                              aria-label={t('commentsCancel')}
                              className={cn(
                                'p-1.5 rounded',
                                colors.textMuted,
                                colors.interactiveHover,
                                'disabled:opacity-50 disabled:cursor-not-allowed',
                              )}
                            >
                              <XIcon className="w-4 h-4" />
                            </button>
                          </>
                        ) : (
                          <>
                            <Button
                              variant="default"
                              size="sm"
                              disabled={savingEdit || !editDraft.trim()}
                              onClick={() => void handleSaveEdit()}
                            >
                              {savingEdit ? t('commentsSaving') : t('commentsSave')}
                            </Button>
                            <Button variant="ghost" size="sm" disabled={savingEdit} onClick={cancelEdit}>
                              {t('commentsCancel')}
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  ) : (
                    <CollaborationNoteBody
                      body={c.body}
                      users={mentionData?.users ?? []}
                    />
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className={cn(isDrawer && 'shrink-0 border-t pt-3', colors.border)}>
        <MentionTextarea
          emailId={emailId}
          partitionAt={partitionAt}
          mentionData={mentionData}
          value={draft}
          onChange={setDraft}
          placeholder={t('commentsPlaceholder')}
          className={mentionTextareaClass}
          rows={3}
          disabled={saving}
          menuZIndex={mentionMenuZIndex}
        />
        <Button variant="default" size="sm" disabled={saving || !draft.trim()} onClick={() => void handleSubmit()} className="mt-2">
          {saving ? t('commentsPosting') : t('commentsPost')}
        </Button>
      </div>

      <ConfirmationDialog
        isOpen={deleteTarget != null}
        onClose={() => !deleting && setDeleteTarget(null)}
        onConfirm={() => void handleDeleteConfirm()}
        title={t('commentsDeleteTitle')}
        message={t('commentsDeleteMessage')}
        confirmText={t('commentsDelete')}
        cancelText={t('commentsCancel')}
        confirmVariant="danger"
        isLoading={deleting}
      />
    </div>
  );
};

export default EmailCommentsPanel;
